import axios from 'axios'

import config from '../../config'
import {
    getEventDetailQuery,
    getGDPRQuery,
    getHallInfoDataQuery,
    getSeatsAndHallQuery,
    getSeatsQuery,
} from '../graphql/queries'
import { getCookie } from '../token/helpers'

export default class Api {
    constructor() {
        this.api_token = null
        this.client = null
        this.api_base_url = process.env.REACT_APP_API_BASE_URL
        this.mail_sender_base_url = process.env.REACT_APP_MAIL_SENDER_API_BASE_URL
        this.cinestar_gql_base_url = process.env.REACT_APP_CINESTAR_GRAPHQL_API_BASE_URL
    }

    init = () => {
        this.api_token = getCookie('token') ?? localStorage.getItem('token')

        let headers = {
            Accept: 'application/json',
        }

        if (this.api_token) {
            headers.token = this.api_token
        }

        this.client = axios.create({
            baseURL: this.api_base_url,
            timeout: 60000,
            headers: headers,
        })

        return this.client
    }

    initCinestarGql = (headers) => {
        this.client = axios.create({
            baseURL: this.cinestar_gql_base_url,
            timeout: 25000,
            headers,
        })

        return this.client
    }

    healthCheck = async () => {
        return await this.init().get(config.api.healthCheck.uri)
    }

    createToken = async () => {
        return await this.init().post(config.api.createToken.uri, { api_key: process.env.REACT_APP_API_KEY })
    }

    getTokenInfo = async () => {
        return await this.init().get(config.api.getToken.uri)
    }

    loginAuthorize = async (params) => {
        return await this.init().post(config.api.login.uri, { ...params })
    }

    logoutAuthorize = async () => {
        return await this.init().get(config.api.logout.uri)
    }

    getAccount = async () => {
        return await this.init().get(config.api.getAccount.uri)
    }

    register = async (params) => {
        return await this.init().post(config.api.register.uri, { ...params })
    }

    gdprSet = async (params) => {
        return await this.init().post(config.api.gdprSet.uri, { ...params })
    }

    changePassword = async (params) => {
        return await this.init().post(config.api.changePassword.uri, { ...params })
    }

    forgottenPassword = async (params) => {
        return await this.init().post(config.api.forgottenPassword.uri, { ...params })
    }

    changeForgottenPassword = async (params) => {
        return await this.init().post(config.api.changeForgottenPassword.uri, { ...params })
    }

    uploadProfileImage = async (params) => {
        return await this.init().post(config.api.uploadProfileImage.uri, { ...params })
    }

    ordersGet = async (params) => {
        return await this.init().post(config.api.ordersGet.uri, { ...params })
    }

    bookingsGet = async (params) => {
        return await this.init().post(config.api.bookingsGet.uri, { ...params })
    }

    loyaltyProgramRegister = async (params) => {
        return await this.init().post(config.api.loyaltyProgramRegister.uri, { ...params })
    }

    loyaltyProgramBlock = async () => {
        return await this.init().get(config.api.loyaltyProgramBlock.uri)
    }

    loyaltyProgramCoupons = async () => {
        return await this.init().get(config.api.loyaltyProgramCoupons.uri)
    }

    loyaltyProgramRewards = async (params) => {
        return await this.init().post(config.api.loyaltyProgramRewards.uri, { ...params })
    }

    loyaltyProgramTransactions = async () => {
        return await this.init().get(config.api.loyaltyProgramTransactions.uri)
    }

    getEventData = async (params) => {
        return await this.init().post(config.api.getEventData.uri, { ...params })
    }

    getGqlEventData = async ({ movieId, site }) => {
        const query = getEventDetailQuery(movieId, site)
        const headers = { Authorization: 'Bearer jAYsrBfTzqmdVDULOuYIYHbW3oov-VPp' }

        return await this.initCinestarGql(headers).post(config.cinestarGql.getGqlEventData.uri, {
            data: { query },
        })
    }

    getGqlSeatsDetails = async ({ site }) => {
        const query = getSeatsAndHallQuery(site)
        const headers = { Authorization: 'Bearer 6wfs77DqJS5LPeFvGJRRtzsGXjjRUqSr' }

        return await this.initCinestarGql(headers).post(config.cinestarGql.getGqlEventData.uri, {
            data: { query },
        })
    }

    getGqlHallDetail = async ({ site, venueid }) => {
        const query = getHallInfoDataQuery(site, venueid)
        const headers = { Authorization: 'Bearer J8HFhOuAGTWLH75OIuOEqq1309F6OvRl' }

        return await this.initCinestarGql(headers).post(config.cinestarGql.getGqlEventData.uri, {
            data: { query },
        })
    }

    getSeats = async ({ site, cinemaHallId }) => {
        const query = getSeatsQuery(site, cinemaHallId)
        const headers = { Authorization: 'Bearer 6wfs77DqJS5LPeFvGJRRtzsGXjjRUqSr' }

        return await this.initCinestarGql(headers).post(config.cinestarGql.getGqlEventData.uri, {
            data: { query },
        })
    }

    getGDPRdata = async (slug) => {
        const query = getGDPRQuery(slug)

        return await this.initCinestarGql().post(config.cinestarGql.getGqlEventData.uri, {
            data: { query },
        })
    }

    getRelatedEvents = async (params) => {
        return await this.init().post(config.api.getRelatedEvents.uri, { ...params })
    }

    getHallData = async (params) => {
        return await this.init().post(config.api.getHallData.uri, { ...params })
    }

    getBasketData = async (params) => {
        return await this.init().post(config.api.getBasketData.uri, { ...params })
    }

    getVouchers = async (params) => {
        return await this.init().post(config.api.getVouchers.uri, { ...params })
    }

    setVoucher = async (params) => {
        return await this.init().post(config.api.setVouchers.uri, { ...params })
    }

    setCardProlonge = async (params) => {
        return await this.init().post(config.api.cardProlonge.uri, { ...params })
    }

    setCardRemove = async (params) => {
        return await this.init().delete(config.api.cardRemove.uri, { data: { ...params } })
    }

    addTicketsToBasket = async (params) => {
        return await this.init().post(config.api.addTicketsToBasket.uri, { ...params })
    }

    removeTicketFromBasket = async (params) => {
        return await this.init().delete(config.api.removeTicketFromBasket.uri, { data: { ...params } })
    }

    changeTicketCategory = async (params) => {
        return await this.init().post(config.api.changeTicketCategory.uri, { ...params })
    }

    ticketVoucherAdd = async (params) => {
        return await this.init().post(config.api.ticketVoucherAdd.uri, { ...params })
    }

    ticketVoucherRemove = async (params) => {
        return await this.init().post(config.api.ticketVoucherRemove.uri, { ...params })
    }

    setPayment = async (params) => {
        return await this.init().post(config.api.setPayment.uri, { ...params })
    }

    cartRelease = async (params) => {
        return await this.init().delete(config.api.cartRelease.uri, { data: { ...params } })
    }

    // BOOKING

    bookingCreate = async (params) => {
        return await this.init().post(config.api.bookingCreate.uri, { ...params })
    }

    bookingGet = async (params) => {
        return await this.init().post(config.api.bookingGet.uri, { ...params })
    }

    bookingCancel = async (params) => {
        return await this.init().post(config.api.bookingCancel.uri, { ...params })
    }

    orderGet = async (params) => {
        return await this.init().post(config.api.orderGet.uri, { ...params })
    }

    bookingRestoreToCard = async (params) => {
        return await this.init().post(config.api.bookingRestoreToCard.uri, { ...params })
    }

    //DOCUMENT - print methods

    eticketGet = async (params) => {
        return await this.init().post(config.api.eticketGet.uri, { ...params })
    }

    evoucherGet = async (params) => {
        return await this.init().post(config.api.evoucherGet.uri, { ...params })
    }

    //SMS

    sendSms = async (params) => {
        return await this.init().post(config.api.sendSms.uri, { ...params })
    }

    //PAYMENT

    paymentCreate = async (params) => {
        return await this.init().post(config.api.paymentCreate.uri, { ...params })
    }

    paymentVerify = async (params) => {
        return await this.init().post(config.api.paymentVerify.uri, { ...params })
    }

    paymentCancel = async (params) => {
        return await this.init().delete(config.api.paymentCancel.uri, { data: { ...params } })
    }

    ticketPointPayment = async (params) => {
        return await this.init().post(config.api.ticketPointPayment.uri, { ...params })
    }

    ticketMoneyPayment = async (params) => {
        return await this.init().post(config.api.ticketMoneyPayment.uri, { ...params })
    }
}
